/**
 * Organization Document Reports Mock Data
 * Used for Phase 4: UI - Reports
 *
 * This file contains mock data for:
 * - Expiring Documents Report
 * - Missing Documents Report
 * - Report summaries and charts
 */

// ============================================
// ORGANIZATIONS LIST (for filter dropdown)
// ============================================

/**
 * List of organizations for filter dropdown
 */
export const mockOrganizationsList = [
  { id: 'org-001', name: 'ABC Trading LLC', code: 'ABC' },
  { id: 'org-002', name: 'XYZ Services LLC', code: 'XYZ' },
  { id: 'org-003', name: 'Global Imports FZE', code: 'GIF' },
  { id: 'org-004', name: 'Tech Solutions FZCO', code: 'TSF' },
  { id: 'org-005', name: 'Premium Logistics LLC', code: 'PLL' },
  { id: 'org-006', name: 'Creative Design Studio', code: 'CDS' },
  { id: 'org-007', name: 'Marine Equipment Trading', code: 'MET' },
  { id: 'org-008', name: 'Food & Beverage Co', code: 'FBC' },
  { id: 'org-010', name: 'Horizon Tech Solutions', code: 'HTS' },
  { id: 'org-011', name: 'Gulf Construction Co', code: 'GCC' },
  { id: 'org-012', name: 'Smart Retail LLC', code: 'SRL' },
  { id: 'org-013', name: 'Elite Consulting FZCO', code: 'ECF' },
  { id: 'org-014', name: 'Premium Auto Parts', code: 'PAP' },
  { id: 'org-015', name: 'Digital Marketing Hub', code: 'DMH' }
];

// ============================================
// EXPIRING DOCUMENTS REPORT
// ============================================

/**
 * Summary of document expiry status across all organizations
 */
export const mockExpiringDocumentsSummary = {
  total: 245,
  expired: 18,
  expiresIn0To7Days: 12,
  expiresIn8To30Days: 28,
  expiresIn31To60Days: 45,
  expiresIn61To90Days: 38,
  valid: 104,
  lastUpdated: new Date().toISOString(),
  _isMock: true
};

/**
 * Expiry status by document type
 */
export const mockExpiringByType = [
  {
    typeCode: 'TRADE_LICENSE',
    typeName: 'Trade License',
    total: 52,
    expired: 4,
    expiresIn30Days: 8,
    expiresIn60Days: 12,
    valid: 28,
    color: '#4CAF50'
  },
  {
    typeCode: 'ESTABLISHMENT_CARD',
    typeName: 'Establishment Card',
    total: 48,
    expired: 3,
    expiresIn30Days: 6,
    expiresIn60Days: 10,
    valid: 29,
    color: '#2196F3'
  },
  {
    typeCode: 'IMMIGRATION_CARD',
    typeName: 'Immigration Card',
    total: 45,
    expired: 5,
    expiresIn30Days: 7,
    expiresIn60Days: 8,
    valid: 25,
    color: '#9C27B0'
  },
  {
    typeCode: 'CT_RETURN',
    typeName: 'Corporate Tax Return',
    total: 38,
    expired: 2,
    expiresIn30Days: 9,
    expiresIn60Days: 11,
    valid: 16,
    color: '#FF5722'
  },
  {
    typeCode: 'VAT_RETURN',
    typeName: 'VAT Return',
    total: 32,
    expired: 2,
    expiresIn30Days: 5,
    expiresIn60Days: 8,
    valid: 17,
    color: '#FF9800'
  },
  {
    typeCode: 'AUDIT_REPORT',
    typeName: 'Audited Financial Statements',
    total: 30,
    expired: 2,
    expiresIn30Days: 5,
    expiresIn60Days: 6,
    valid: 17,
    color: '#607D8B'
  }
];

/**
 * List of expiring/expired documents with details
 */
export const mockExpiringDocumentsList = [
  {
    id: 'doc-exp-001',
    organisation: { id: 'org-001', name: 'ABC Trading LLC', code: 'ABC' },
    documentType: { code: 'TRADE_LICENSE', name: 'Trade License' },
    documentNumber: 'DED-123456',
    issueDate: '2024-01-15',
    expiryDate: '2025-01-10',
    daysToExpiry: -20,
    status: 'EXPIRED',
    issuingAuthority: 'DED',
    _isMock: true
  },
  {
    id: 'doc-exp-002',
    organisation: { id: 'org-002', name: 'XYZ Services LLC', code: 'XYZ' },
    documentType: { code: 'ESTABLISHMENT_CARD', name: 'Establishment Card' },
    documentNumber: 'MOH-234567',
    issueDate: '2024-02-01',
    expiryDate: '2025-02-01',
    daysToExpiry: 2,
    status: 'EXPIRES_URGENTLY',
    issuingAuthority: 'MOHRE',
    _isMock: true
  },
  {
    id: 'doc-exp-003',
    organisation: { id: 'org-003', name: 'Global Imports FZE', code: 'GIF' },
    documentType: { code: 'CT_RETURN', name: 'Corporate Tax Return' },
    documentNumber: 'CT-2024-001',
    issueDate: '2024-09-01',
    expiryDate: '2025-02-05',
    daysToExpiry: 6,
    status: 'EXPIRES_URGENTLY',
    issuingAuthority: 'FTA',
    _isMock: true
  },
  {
    id: 'doc-exp-004',
    organisation: { id: 'org-004', name: 'Tech Solutions FZCO', code: 'TSF' },
    documentType: { code: 'TRADE_LICENSE', name: 'Trade License' },
    documentNumber: 'DMCC-345678',
    issueDate: '2024-02-15',
    expiryDate: '2025-02-15',
    daysToExpiry: 16,
    status: 'EXPIRES_SOON',
    issuingAuthority: 'DMCC',
    _isMock: true
  },
  {
    id: 'doc-exp-005',
    organisation: { id: 'org-001', name: 'ABC Trading LLC', code: 'ABC' },
    documentType: { code: 'IMMIGRATION_CARD', name: 'Immigration Card' },
    documentNumber: 'GDRFA-456789',
    issueDate: '2024-02-20',
    expiryDate: '2025-02-20',
    daysToExpiry: 21,
    status: 'EXPIRES_SOON',
    issuingAuthority: 'GDRFA',
    _isMock: true
  },
  {
    id: 'doc-exp-006',
    organisation: { id: 'org-005', name: 'Premium Logistics LLC', code: 'PLL' },
    documentType: { code: 'VAT_RETURN', name: 'VAT Return' },
    documentNumber: 'VAT-Q4-2024',
    issueDate: '2024-10-01',
    expiryDate: '2025-02-28',
    daysToExpiry: 29,
    status: 'EXPIRES_SOON',
    issuingAuthority: 'FTA',
    _isMock: true
  },
  {
    id: 'doc-exp-007',
    organisation: { id: 'org-006', name: 'Creative Design Studio', code: 'CDS' },
    documentType: { code: 'ESTABLISHMENT_CARD', name: 'Establishment Card' },
    documentNumber: 'MOH-567890',
    issueDate: '2024-03-15',
    expiryDate: '2025-03-15',
    daysToExpiry: 44,
    status: 'EXPIRES_IN_60',
    issuingAuthority: 'MOHRE',
    _isMock: true
  },
  {
    id: 'doc-exp-008',
    organisation: { id: 'org-007', name: 'Marine Equipment Trading', code: 'MET' },
    documentType: { code: 'AUDIT_REPORT', name: 'Audited Financial Statements' },
    documentNumber: null,
    issueDate: '2024-04-01',
    expiryDate: '2025-04-01',
    daysToExpiry: 61,
    status: 'EXPIRES_IN_90',
    issuingAuthority: 'PWC Dubai',
    _isMock: true
  },
  {
    id: 'doc-exp-009',
    organisation: { id: 'org-002', name: 'XYZ Services LLC', code: 'XYZ' },
    documentType: { code: 'TRADE_LICENSE', name: 'Trade License' },
    documentNumber: 'DED-678901',
    issueDate: '2024-01-05',
    expiryDate: '2025-01-05',
    daysToExpiry: -25,
    status: 'EXPIRED',
    issuingAuthority: 'DED',
    _isMock: true
  },
  {
    id: 'doc-exp-010',
    organisation: { id: 'org-008', name: 'Food & Beverage Co', code: 'FBC' },
    documentType: { code: 'IMMIGRATION_CARD', name: 'Immigration Card' },
    documentNumber: 'GDRFA-789012',
    issueDate: '2024-01-01',
    expiryDate: '2025-01-01',
    daysToExpiry: -29,
    status: 'EXPIRED',
    issuingAuthority: 'GDRFA',
    _isMock: true
  }
];

// ============================================
// MISSING DOCUMENTS REPORT
// ============================================

/**
 * Summary of missing documents across all organizations
 */
export const mockMissingDocumentsSummary = {
  totalOrganizations: 120,
  organizationsWithMissing: 32,
  organizationsCompliant: 88,
  complianceRate: 73.3,
  totalMissingDocuments: 58,
  criticalMissing: 24,
  lastUpdated: new Date().toISOString(),
  _isMock: true
};

/**
 * Missing documents by document type
 */
export const mockMissingByType = [
  {
    typeCode: 'IMMIGRATION_CARD',
    typeName: 'Immigration Card',
    missingCount: 18,
    isMandatory: true,
    reason: 'Required for companies sponsoring visas',
    color: '#f44336'
  },
  {
    typeCode: 'TRN_CERTIFICATE',
    typeName: 'Tax Registration Certificate',
    missingCount: 14,
    isMandatory: true,
    reason: 'Required for all UAE companies',
    color: '#FF5722'
  },
  {
    typeCode: 'VAT_CERTIFICATE',
    typeName: 'VAT Registration Certificate',
    missingCount: 12,
    isMandatory: true,
    reason: 'Required for VAT registered companies',
    color: '#FF9800'
  },
  {
    typeCode: 'CHAMBER_CERT',
    typeName: 'Chamber of Commerce Certificate',
    missingCount: 8,
    isMandatory: false,
    reason: 'Recommended for trade companies',
    color: '#FFC107'
  },
  {
    typeCode: 'AUDIT_REPORT',
    typeName: 'Audited Financial Statements',
    missingCount: 6,
    isMandatory: true,
    reason: 'Required for companies with revenue > AED 50M',
    color: '#607D8B'
  }
];

/**
 * Organizations with missing documents
 */
export const mockOrganizationsWithMissing = [
  {
    id: 'org-miss-001',
    organisation: { id: 'org-010', name: 'Horizon Tech Solutions', code: 'HTS' },
    totalRequired: 5,
    uploaded: 2,
    missing: 3,
    complianceScore: 40,
    missingDocuments: [
      { typeCode: 'IMMIGRATION_CARD', typeName: 'Immigration Card', isMandatory: true, reason: 'sponsorsVisas = true' },
      { typeCode: 'TRN_CERTIFICATE', typeName: 'Tax Registration Certificate', isMandatory: true },
      { typeCode: 'VAT_CERTIFICATE', typeName: 'VAT Registration Certificate', isMandatory: true, reason: 'vatRegistered = true' }
    ],
    _isMock: true
  },
  {
    id: 'org-miss-002',
    organisation: { id: 'org-011', name: 'Gulf Construction Co', code: 'GCC' },
    totalRequired: 6,
    uploaded: 4,
    missing: 2,
    complianceScore: 67,
    missingDocuments: [
      { typeCode: 'IMMIGRATION_CARD', typeName: 'Immigration Card', isMandatory: true, reason: 'sponsorsVisas = true' },
      { typeCode: 'AUDIT_REPORT', typeName: 'Audited Financial Statements', isMandatory: true, reason: 'revenue > 50M' }
    ],
    _isMock: true
  },
  {
    id: 'org-miss-003',
    organisation: { id: 'org-012', name: 'Smart Retail LLC', code: 'SRL' },
    totalRequired: 4,
    uploaded: 2,
    missing: 2,
    complianceScore: 50,
    missingDocuments: [
      { typeCode: 'TRN_CERTIFICATE', typeName: 'Tax Registration Certificate', isMandatory: true },
      { typeCode: 'ESTABLISHMENT_CARD', typeName: 'Establishment Card', isMandatory: true }
    ],
    _isMock: true
  },
  {
    id: 'org-miss-004',
    organisation: { id: 'org-013', name: 'Elite Consulting FZCO', code: 'ECF' },
    totalRequired: 5,
    uploaded: 4,
    missing: 1,
    complianceScore: 80,
    missingDocuments: [
      { typeCode: 'CHAMBER_CERT', typeName: 'Chamber of Commerce Certificate', isMandatory: false }
    ],
    _isMock: true
  },
  {
    id: 'org-miss-005',
    organisation: { id: 'org-014', name: 'Premium Auto Parts', code: 'PAP' },
    totalRequired: 5,
    uploaded: 3,
    missing: 2,
    complianceScore: 60,
    missingDocuments: [
      { typeCode: 'IMMIGRATION_CARD', typeName: 'Immigration Card', isMandatory: true, reason: 'sponsorsVisas = true' },
      { typeCode: 'VAT_CERTIFICATE', typeName: 'VAT Registration Certificate', isMandatory: true }
    ],
    _isMock: true
  },
  {
    id: 'org-miss-006',
    organisation: { id: 'org-015', name: 'Digital Marketing Hub', code: 'DMH' },
    totalRequired: 4,
    uploaded: 3,
    missing: 1,
    complianceScore: 75,
    missingDocuments: [
      { typeCode: 'TRN_CERTIFICATE', typeName: 'Tax Registration Certificate', isMandatory: true }
    ],
    _isMock: true
  }
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get expiry status color
 */
export const getExpiryStatusColor = (status) => {
  switch (status) {
    case 'EXPIRED':
      return '#f44336'; // red
    case 'EXPIRES_URGENTLY':
      return '#ff5722'; // deep orange
    case 'EXPIRES_SOON':
      return '#ff9800'; // orange
    case 'EXPIRES_IN_60':
      return '#ffc107'; // amber
    case 'EXPIRES_IN_90':
      return '#8bc34a'; // light green
    case 'VALID':
      return '#4caf50'; // green
    default:
      return '#9e9e9e'; // grey
  }
};

/**
 * Get expiry status label
 */
export const getExpiryStatusLabel = (status) => {
  switch (status) {
    case 'EXPIRED':
      return 'Expired';
    case 'EXPIRES_URGENTLY':
      return 'Expires < 7 days';
    case 'EXPIRES_SOON':
      return 'Expires in 8-30 days';
    case 'EXPIRES_IN_60':
      return 'Expires in 31-60 days';
    case 'EXPIRES_IN_90':
      return 'Expires in 61-90 days';
    case 'VALID':
      return 'Valid';
    default:
      return 'Unknown';
  }
};

/**
 * Get compliance score color
 */
export const getComplianceScoreColor = (score) => {
  if (score >= 80) return '#4caf50'; // green
  if (score >= 60) return '#ff9800'; // orange
  if (score >= 40) return '#ff5722'; // deep orange
  return '#f44336'; // red
};

/**
 * Filter expiring documents by criteria
 */
export const filterExpiringDocuments = (documents, filters = {}) => {
  let filtered = [...documents];

  // Filter by organization ID (dropdown)
  if (filters.organizationId && filters.organizationId !== 'ALL') {
    filtered = filtered.filter(doc => doc.organisation.id === filters.organizationId);
  }

  if (filters.status && filters.status !== 'ALL') {
    filtered = filtered.filter(doc => doc.status === filters.status);
  }

  if (filters.documentType && filters.documentType !== 'ALL') {
    filtered = filtered.filter(doc => doc.documentType.code === filters.documentType);
  }

  // Text search for organization name/code
  if (filters.organization) {
    filtered = filtered.filter(doc =>
      doc.organisation.name.toLowerCase().includes(filters.organization.toLowerCase()) ||
      doc.organisation.code.toLowerCase().includes(filters.organization.toLowerCase())
    );
  }

  if (filters.sortBy) {
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'daysToExpiry':
          return a.daysToExpiry - b.daysToExpiry;
        case 'organization':
          return a.organisation.name.localeCompare(b.organisation.name);
        case 'documentType':
          return a.documentType.name.localeCompare(b.documentType.name);
        default:
          return 0;
      }
    });
  }

  return filtered;
};

/**
 * Filter missing documents report
 */
export const filterMissingDocuments = (organizations, filters = {}) => {
  let filtered = [...organizations];

  // Filter by organization ID (dropdown)
  if (filters.organizationId && filters.organizationId !== 'ALL') {
    filtered = filtered.filter(org => org.organisation.id === filters.organizationId);
  }

  if (filters.documentType && filters.documentType !== 'ALL') {
    filtered = filtered.filter(org =>
      org.missingDocuments.some(doc => doc.typeCode === filters.documentType)
    );
  }

  if (filters.mandatoryOnly) {
    filtered = filtered.filter(org =>
      org.missingDocuments.some(doc => doc.isMandatory)
    );
  }

  // Text search for organization name/code
  if (filters.organization) {
    filtered = filtered.filter(org =>
      org.organisation.name.toLowerCase().includes(filters.organization.toLowerCase()) ||
      org.organisation.code.toLowerCase().includes(filters.organization.toLowerCase())
    );
  }

  if (filters.sortBy) {
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'complianceScore':
          return a.complianceScore - b.complianceScore;
        case 'missingCount':
          return b.missing - a.missing;
        case 'organization':
          return a.organisation.name.localeCompare(b.organisation.name);
        default:
          return 0;
      }
    });
  }

  return filtered;
};

/**
 * Generate CSV export data for expiring documents
 */
export const generateExpiringDocumentsCsv = (documents) => {
  const headers = ['Organization', 'Document Type', 'Document Number', 'Issue Date', 'Expiry Date', 'Days to Expiry', 'Status', 'Issuing Authority'];

  const rows = documents.map(doc => [
    doc.organisation.name,
    doc.documentType.name,
    doc.documentNumber || 'N/A',
    doc.issueDate,
    doc.expiryDate,
    doc.daysToExpiry,
    getExpiryStatusLabel(doc.status),
    doc.issuingAuthority
  ]);

  return [headers, ...rows].map(row => row.join(',')).join('\n');
};

/**
 * Generate CSV export data for missing documents
 */
export const generateMissingDocumentsCsv = (organizations) => {
  const headers = ['Organization', 'Total Required', 'Uploaded', 'Missing', 'Compliance %', 'Missing Documents'];

  const rows = organizations.map(org => [
    org.organisation.name,
    org.totalRequired,
    org.uploaded,
    org.missing,
    org.complianceScore + '%',
    org.missingDocuments.map(d => d.typeName).join('; ')
  ]);

  return [headers, ...rows].map(row => row.join(',')).join('\n');
};

/**
 * Simulate downloading a file
 */
export const simulateDownload = (content, filename) => {
  // In real implementation, this would trigger actual download
  console.log(`[MOCK] Downloading ${filename}...`);
  console.log(`[MOCK] Content: ${content.substring(0, 200)}...`);
  return { success: true, filename, _isMock: true };
};

/**
 * Simulate sending email report
 */
export const simulateSendEmailReport = (reportType, recipients) => {
  console.log(`[MOCK] Sending ${reportType} report to: ${recipients.join(', ')}`);
  return {
    success: true,
    message: `Report sent to ${recipients.length} recipient(s)`,
    _isMock: true
  };
};

// Default export
export default {
  expiringDocumentsSummary: mockExpiringDocumentsSummary,
  expiringByType: mockExpiringByType,
  expiringDocumentsList: mockExpiringDocumentsList,
  missingDocumentsSummary: mockMissingDocumentsSummary,
  missingByType: mockMissingByType,
  organizationsWithMissing: mockOrganizationsWithMissing
};
